import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAccessibleCategories } from '@/hooks/useAccessibleCategories';
import { getCategoryTier } from '@/config/categoryTiers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Play, Sparkles, TrendingUp, Clock, Users } from 'lucide-react';
import UserGuidance from '@/components/UserGuidance';
import SmartRecommendationsPanel from '@/components/SmartRecommendationsPanel';
import { CreditBalanceCard } from '@/components/home/CreditBalanceCard';
import { CommunitySessions } from '@/components/community/CommunitySessions';

import { backendClient } from '@/lib/backendClient';

const CleanHomePage = () => {
  const { user } = useAuth();
  const { accessibleCategories, loading, userRole } = useAccessibleCategories();
  const [showWelcome, setShowWelcome] = React.useState(!localStorage.getItem('welcomeDismissed'));

  // Debug logging to confirm home page is shown for authenticated users
  console.log('🏠 Clean Home Page rendered for user:', user?.id || 'unknown');

  // Show all categories for admins, limit to 6 for other users
  const featuredCategories = userRole === 'admin' 
    ? accessibleCategories 
    : accessibleCategories.slice(0, 6);
  const userName = user?.email?.split('@')[0] || 'Friend';

  const getCategoryLEDStyle = (categoryId: string): string => {
    // Meditation gets electric blue LED
    if (categoryId === 'meditation') return 'category-card-led-meditation';
    
    // Anxiety gets purple LED
    if (categoryId === 'anxiety') return 'category-card-led-anxiety';
    
    // Massage gets pink LED  
    if (categoryId === 'massage') return 'category-card-led-massage';
    
    // Memory gets green LED
    if (categoryId === 'memory') return 'category-card-led-memory';
    
    // Motivation gets orange LED
    if (categoryId === 'motivation') return 'category-card-led-motivation';
    
    // Sleep gets indigo LED
    if (categoryId === 'sleep') return 'category-card-led-sleep';
    
    // Be Still gets meditation blue LED  
    if (categoryId === 'be_still') return 'category-card-led-meditation';
    
    // Self Love gets pink LED
    if (categoryId === 'self_love') return 'category-card-led-massage';

    // Yoga gets meditation blue LED
    if (categoryId === 'yoga-meditation') return 'category-card-led-meditation';
    
    // Default LED
    return 'category-card-led-default';
  };

  const handleDismissWelcome = () => {
    localStorage.setItem('welcomeDismissed', 'true');
    setShowWelcome(false);
  };

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-6 py-8 max-w-7xl">
        {/* Hero Section */}
        <div className="text-center mb-12 space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-golden-dark/20 to-golden-light/20 border border-golden-light/30 mb-4">
            <Sparkles className="h-4 w-4 text-golden-light animate-pulse" />
            <span className="text-sm font-medium text-golden-light">AI-Powered Wellness Sessions</span>
          </div>
          
          <h1 className="text-5xl font-bold bg-gradient-hero bg-clip-text text-transparent tracking-tight mb-6">
            Welcome back, {userName}
          </h1>
          
          <p className="text-xl text-muted-foreground/90 font-light max-w-4xl mx-auto leading-relaxed mb-8">
            Discover personalized meditation and wellness sessions crafted by advanced AI. 
            Each session is uniquely tailored to your preferences, goals, and personal journey.
          </p>

          {/* Quick Stats */}
          <div className="flex flex-wrap justify-center gap-6 mb-12">
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-golden-dark/10 to-golden-light/10 border border-golden-light/20">
              <TrendingUp className="h-5 w-5 text-golden-light" />
              <span className="text-sm font-medium text-golden-light">Personalized AI</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-golden-dark/10 to-golden-light/10 border border-golden-light/20">
              <Clock className="h-5 w-5 text-golden-light" />
              <span className="text-sm font-medium text-golden-light">3-15 Minutes</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-golden-dark/10 to-golden-light/10 border border-golden-light/20">
              <Users className="h-5 w-5 text-golden-light" />
              <span className="text-sm font-medium text-golden-light">Individual & Group</span>
            </div>
          </div>
        </div>

        {/* Credit Balance Card */}
        <div className="mb-12 max-w-4xl mx-auto">
          <CreditBalanceCard />
        </div>

        {/* Smart Recommendations */}
        <div className="mb-12">
          <SmartRecommendationsPanel
            maxRecommendations={3}
            className="animate-fade-in"
          />
        </div>

        {/* Community Sessions */}
        <div className="mb-12">
          <CommunitySessions
            limit={6}
            featuredOnly={true}
          />
        </div>

        {/* Welcome Tutorial for New Users */}
        {showWelcome && (
          <UserGuidance 
            variant="welcome" 
            onClose={handleDismissWelcome}
          />
        )}

        {/* Featured Categories */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold bg-gradient-hero bg-clip-text text-transparent">
              Choose Your Wellness Journey
            </h2>
            <Button
              asChild
              variant="ghost"
              className="text-golden-light hover:text-golden-light/90 hover:bg-golden-light/10 transition-all duration-300"
            >
              <Link to="/categories">View All Categories</Link>
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredCategories.map((category) => (
              <Link key={category.id} to={`/category/${category.id}`} className="block group">
                <Card className={`
                  category-card-animated
                  ${getCategoryLEDStyle(category.id)}
                  cursor-pointer h-full transition-all duration-500 ease-out
                  animate-fade-in
                `}>
                  <CardHeader className="text-center pb-4 relative">
                    {userRole === 'admin' && getCategoryTier(category.id) === 'practitioner' && (
                      <div className="absolute top-4 right-4">
                        <span className="text-xs bg-gradient-to-r from-golden-light to-golden-dark text-background px-2 py-1 rounded-full font-bold shadow-lg">
                          PRO
                        </span>
                      </div>
                    )}
                    <div className="mb-6 relative">
                      {typeof category.icon === 'function' ? (
                        React.createElement(category.icon, { 
                          className: "h-16 w-16 mx-auto text-golden-light category-icon filter drop-shadow-lg" 
                        })
                      ) : (
                        <div className="h-16 w-16 mx-auto bg-golden-light/20 rounded-full category-icon"></div>
                      )}
                    </div>
                    <CardTitle className="text-2xl font-bold bg-gradient-hero bg-clip-text text-transparent group-hover:bg-gradient-primary transition-all duration-500 mb-3">
                      {category.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-center pt-0 pb-8">
                    <CardDescription className="text-base text-muted-foreground/90 font-light leading-relaxed mb-6 min-h-[3rem]">
                      {category.description}
                    </CardDescription>
                    
                    {/* Call to Action */}
                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-3 group-hover:translate-y-0">
                      <Button
                        size="lg"
                        className="w-full rounded-xl font-semibold hover:scale-[1.02] transition-all duration-300 shadow-lg"
                        variant="accent"
                      >
                        <Play className="h-5 w-5 mr-2" />
                        Start Session
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Quick Start Section */}
        <div className="bg-gradient-to-br from-golden-dark/10 to-golden-light/10 rounded-3xl p-8 border border-golden-light/20 backdrop-blur-sm">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-golden-light/20 text-golden-dark font-semibold">
              <Sparkles className="h-4 w-4" />
              Quick Start
            </div>
            
            <h3 className="text-2xl font-bold bg-gradient-hero bg-clip-text text-transparent">
              Ready to Begin Your Wellness Journey?
            </h3>
            
            <p className="text-lg text-muted-foreground/90 font-light max-w-2xl mx-auto">
              Start with a quick meditation session or explore our full range of personalized wellness experiences.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Button
                asChild
                size="lg"
                className="rounded-xl font-bold text-lg px-8 py-3 hover:scale-[1.02] transition-all duration-300 shadow-lg"
                variant="default"
              >
                <Link to="/category/meditation">
                  <Play className="h-5 w-5 mr-2" />
                  Quick Meditation
                </Link>
              </Button>
              
              <Button
                asChild
                variant="outline"
                size="lg"
                className="rounded-xl font-semibold border-golden-light/30 text-golden-light hover:bg-golden-light/10 hover:border-golden-light/50 hover:scale-105 transition-all duration-300"
              >
                <Link to="/profile">
                  Personalize Experience
                </Link>
              </Button>
            </div>
          </div>
        </div>
        
        {/* Floating Help */}
        <UserGuidance variant="floating" />
      </div>
    </div>
  );
};

export default CleanHomePage;